import { ADMIN_ENDPOINTS } from './endpoints.ts';
import { apiGet } from '../../api/oauth/helpers.ts';

export interface AuditEvent {
  id: string;
  created_at: string;
  organization_id?: string | null;
  organization_name?: string | null;
  actor_user_id?: number | null;
  actor_username?: string | null;
  actor_role?: string | null;
  target_user_id?: number | null;
  target_username?: string | null;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | string;
  resource_type: string;
  resource_id?: string | null;
  resource_label?: string | null;
  summary: string;
  http_method?: string | null;
  http_path?: string | null;
  request_id?: string | null;
  duration_ms?: number | null;
  user_agent?: string | null;
  before_data?: unknown;
  after_data?: unknown;
  metadata?: unknown;
}

export interface AuditPagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface AuditCounts {
  total: number;
  create: number;
  update: number;
  delete: number;
}

export interface AuditEventsListResponse {
  events: AuditEvent[];
  pagination: AuditPagination;
  counts: AuditCounts;
}

export interface AuditEventListParams {
  search?: string;
  organization_id?: string;
  action?: string;
  resource_type?: string;
  actor_user_id?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}

function buildAuditQuery(params: AuditEventListParams = {}): Record<string, string | number> {
  const query: Record<string, string | number> = {};
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.organization_id?.trim()) query.organization_id = params.organization_id.trim();
  if (params.action?.trim()) query.action = params.action.trim();
  if (params.resource_type?.trim()) query.resource_type = params.resource_type.trim();
  if (params.actor_user_id?.trim()) query.actor_user_id = params.actor_user_id.trim();
  if (params.from_date?.trim()) query.from_date = params.from_date.trim();
  if (params.to_date?.trim()) query.to_date = params.to_date.trim();
  if (params.page) query.page = params.page;
  if (params.page_size) query.page_size = params.page_size;
  return query;
}

export async function fetchAuditEvents(params: AuditEventListParams = {}): Promise<AuditEventsListResponse> {
  const data = await apiGet(ADMIN_ENDPOINTS.AUDIT_EVENTS, buildAuditQuery(params));
  return {
    events: Array.isArray(data?.events) ? data.events : [],
    pagination: data?.pagination ?? { page: 1, page_size: 20, total_items: 0, total_pages: 0 },
    counts: data?.counts ?? { total: 0, create: 0, update: 0, delete: 0 },
  };
}

/** Org-admin list: server forces current org (organization_id query ignored). */
export async function fetchOrgAuditEvents(params: AuditEventListParams = {}): Promise<AuditEventsListResponse> {
  const { organization_id: _ignored, ...rest } = params;
  const data = await apiGet(ADMIN_ENDPOINTS.ORG_AUDIT_EVENTS, buildAuditQuery(rest));
  return {
    events: Array.isArray(data?.events) ? data.events : [],
    pagination: data?.pagination ?? { page: 1, page_size: 20, total_items: 0, total_pages: 0 },
    counts: data?.counts ?? { total: 0, create: 0, update: 0, delete: 0 },
  };
}

export async function fetchAuditEventById(id: string): Promise<AuditEvent> {
  const data = await apiGet(ADMIN_ENDPOINTS.AUDIT_EVENT_BY_ID(id));
  return data?.event as AuditEvent;
}
